import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, HeartPulse, ShieldCheck } from "lucide-react";
import { Logo } from "../components/branding/Logo";

const HERO =
    "https://images.unsplash.com/photo-1611843275167-a9bba9aa65dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHxlbGVnYW50JTIwY2F0JTIwbG9va2luZyUyMGF0JTIwY2FtZXJhfGVufDB8fHx8MTc4NTMyODIxNXww&ixlib=rb-4.1.0&q=85";

/**
 * Minimal branded landing at "/" — the real product deeplinks land on
 * /health-check. This is the placeholder until the embed docs page ships
 * in Phase 3.
 */
export default function Landing() {
    return (
        <div className="meow-shell" data-testid="landing-page">
            <header className="flex items-center justify-between px-5 pt-5">
                <Logo />
                <a
                    href="https://meowmd.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[13px] font-medium text-meow-muted hover:text-meow-charcoal"
                    data-testid="landing-visit-app-link"
                >
                    meowmd.in
                </a>
            </header>

            <div className="relative -mt-1 h-[280px] overflow-hidden">
                <img
                    src={HERO}
                    alt="An elegant tabby cat looking at the camera"
                    className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-meow-cream" />
            </div>

            <div className="mt-2 px-5 pb-32">
                <span className="eyebrow" data-testid="landing-eyebrow">
                    MEOWMD WEB TOOLS
                </span>
                <h1
                    className="mt-3 font-display text-[2.4rem] font-medium leading-[1.05] text-meow-charcoal"
                    data-testid="landing-headline"
                >
                    A friendly second look at your cat&apos;s health.
                </h1>
                <p className="mt-3 text-[15px] leading-relaxed text-meow-muted">
                    Free, five minutes, powered by Claude Sonnet & Haiku. Get a
                    personalised written report with an eight-category breakdown,
                    diet plan, and next-steps advice.
                </p>

                <div className="mt-6 grid gap-2.5">
                    <TileFeature icon={<HeartPulse className="h-4 w-4" />} text="7 baseline + 10 personalised questions" />
                    <TileFeature icon={<Camera className="h-4 w-4" />} text="Optional AI photo check for visible signs" />
                    <TileFeature icon={<ShieldCheck className="h-4 w-4" />} text="Educational only — not veterinary advice" />
                </div>
            </div>

            <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30">
                <div className="mx-auto max-w-[440px] bg-gradient-to-t from-meow-cream via-meow-cream/95 to-transparent px-5 pb-5 pt-8">
                    <Link
                        to="/health-check"
                        className="meow-btn-primary pointer-events-auto w-full"
                        data-testid="landing-start-btn"
                    >
                        Start the free check
                        <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
                    </Link>
                </div>
            </div>
        </div>
    );
}

function TileFeature({ icon, text }) {
    return (
        <div className="flex items-center gap-3 rounded-2xl bg-white p-3.5 shadow-[0_6px_20px_rgba(42,42,42,0.04)]">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-meow-icon text-meow-teal">
                {icon}
            </div>
            <div className="text-[14px] font-medium text-meow-charcoal">{text}</div>
        </div>
    );
}
